import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Loader from "../../components/Loader";
import { getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";

const GREEN = "#03B449";

// MUI Dialog rather than react-bootstrap Modal so it inherits the admin's dark
// theme -- a light-styled modal renders its empty bar tracks as solid white
// blocks and drops the muted text to invisible.
const SurveyResultsModal = ({ show, onHide, survey }) => {
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!show || !survey?.id) return;
    let active = true;
    (async () => {
      setIsLoading(true);
      try {
        const res = await getAllData(`${endPoints.api.SURVEY_RESULTS}${survey.id}`);
        if (active && (res?.status === "success" || res?.status === "SUCCESS")) {
          setOptions(res.data || []);
        } else if (active) {
          setOptions([]);
        }
      } catch (e) {
        if (active) setOptions([]);
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [show, survey?.id]);

  const total = options.reduce((sum, o) => sum + Number(o?.votes || 0), 0);
  const top = options.reduce((m, o) => Math.max(m, Number(o?.votes || 0)), 0);

  return (
    <Dialog maxWidth="sm" fullWidth open={!!show} onClose={onHide}>
      <DialogTitle sx={{ pr: 6 }}>
        Results
        <IconButton
          aria-label="close"
          onClick={onHide}
          sx={{ position: "absolute", right: 8, top: 8, color: "#64607a" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <Typography sx={{ fontWeight: 600, mb: 0.5 }}>{survey?.question}</Typography>
        <Typography variant="body2" sx={{ opacity: 0.7, mb: 2.5 }}>
          {total} {total === 1 ? "response" : "responses"}
        </Typography>

        {options.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            No responses yet.
          </Typography>
        ) : (
          options.map((o) => {
            const votes = Number(o?.votes || 0);
            const pct = total > 0 ? (votes / total) * 100 : 0;
            const leading = votes > 0 && votes === top;
            return (
              <Box key={o.index} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: leading ? 600 : 400 }}>
                    {o.label}
                  </Typography>
                  <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                    {votes} · {pct.toFixed(1)}%
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    // Subtle overlay so an empty track reads as a groove, not a bar.
                    background: "rgba(255,255,255,0.10)",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      width: `${pct}%`,
                      background: GREEN,
                      opacity: leading ? 1 : 0.55,
                      transition: "width .3s ease",
                    }}
                  />
                </Box>
              </Box>
            );
          })
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SurveyResultsModal;
